// OWNER: Agent C. App shell — Kumo Sidebar, collapses to a mobile sheet under
// 768px automatically so the dashboard stays usable at 375px.
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import {
  Sidebar,
  SidebarProvider,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarTrigger,
} from "@cloudflare/kumo/components/sidebar";
import { Text } from "@cloudflare/kumo/components/text";
import {
  HouseIcon,
  ChartLineUpIcon,
  ArrowsLeftRightIcon,
  BankIcon,
  UsersThreeIcon,
  GearIcon,
  type Icon,
} from "@phosphor-icons/react";
import { useIsAdmin } from "../lib/hooks";

// User-supplied brand mark, used for both the favicon (index.html) and here.
const LOGO_URL = "https://static.wikia.nocookie.net/harrypotter/images/9/96/GringottsLogoDH.png/revision/latest?cb=20130314200534";

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  adminOnly?: boolean;
  devOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: HouseIcon },
  { to: "/investments", label: "Investments", icon: ChartLineUpIcon },
  { to: "/transactions", label: "Transactions", icon: ArrowsLeftRightIcon, adminOnly: true },
  { to: "/loans", label: "Loans", icon: BankIcon },
  { to: "/admin", label: "Admin", icon: UsersThreeIcon, adminOnly: true },
  { to: "/settings", label: "Settings", icon: GearIcon, devOnly: true },
];

export function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  return (
    // Kumo's <aside> is `h-full` against this wrapper, not viewport-fixed --
    // without an explicit height here it only grows to match its own short
    // content, so on any page taller than one screen the whole wrapper
    // (sidebar included) scrolled away with the page. Pinning the wrapper to
    // the viewport and scrolling only <main> internally is what keeps the
    // sidebar visually full-height and in place.
    <SidebarProvider defaultOpen className="h-svh overflow-hidden">
      <Sidebar>
        <SidebarHeader>
          <div className="flex items-center gap-2 px-2 py-1">
            <img src={LOGO_URL} alt="" className="h-6 w-6 shrink-0 object-contain" />
            <span className="text-lg font-semibold" style={{ color: "#D4AF37" }}>
              Gringotts
            </span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenu>
            {NAV.filter((item) => (!item.adminOnly || isAdmin) && (!item.devOnly || import.meta.env.DEV)).map((item) => {
              const active = item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);
              return (
                <SidebarMenuButton
                  key={item.to}
                  icon={item.icon}
                  active={active}
                  onClick={() => navigate(item.to)}
                >
                  {item.label}
                </SidebarMenuButton>
              );
            })}
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter>
          <SidebarTrigger />
        </SidebarFooter>
      </Sidebar>
      <main className="flex min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
        <div className="flex items-center gap-2 border-b border-kumo-line p-2 md:hidden">
          <SidebarTrigger />
          <span className="text-base font-semibold" style={{ color: "#D4AF37" }}>
            Gringotts
          </span>
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </SidebarProvider>
  );
}

/** Dev-only page (Settings currently just holds test-data switches) --
 * redirects home outside of `npm run dev`. */
export function RequireDev({ children }: { children: ReactNode }) {
  if (!import.meta.env.DEV) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function PageError({ message }: { message: string }) {
  return (
    <div className="p-8">
      <Text variant="error">{message}</Text>
    </div>
  );
}
